import type { Defesa, NotasDefesa } from '@/interfaces/defesa';
import api from '@/services/api';

export interface DefesasListResponse {
  data: Defesa[];
  message?: string;
}

export interface RespostaDefesaRequest {
  acao: 'APROVAR' | 'REJEITAR';
  justificativa?: string;
}

export interface RespostaDefesaResponse {
  message: string;
  data?: any;
}

export const defesaService = {
  async listarDefesas(params?: { status?: string }): Promise<DefesasListResponse> {
    const { data } = await api.get<DefesasListResponse>('/defesas', { params });
    return data;
  },

  async obterDefesa(id: number): Promise<Defesa> {
    const { data } = await api.get<{ data: Defesa }>(`/defesas/${id}`);
    return data.data;
  },

  async entrarSala(id: number): Promise<{ url: string }> {
    const response = await api.post(`/defesas/${id}/entrar`);
    return response.data;
  },

  async encerrarSalaManualmente(id: number): Promise<Defesa> {
    const { data } = await api.put<{ data: Defesa }>(`/defesas/${id}/encerrar-sala`);
    return data.data;
  },

  async obterLinkConvidados(id: number): Promise<{ url: string }> {
    const { data } = await api.get<{ url: string }>(`/defesas/${id}/link-convidados`);
    return data;
  },

  async entrarSalaConvidado(id: string, token: string, nome: string): Promise<{ url: string }> {
    const { data } = await api.get<{ url: string }>(
      `/defesas/${encodeURIComponent(id)}/conferencia-web`,
      { params: { token, nome } },
    );
    return data;
  },

  async downloadVersaoSubmetida(id: number): Promise<Blob> {
    const { data } = await api.get(`/defesas/${id}/download-versao-submetida`, {
      responseType: 'blob'
    });
    return data;
  },

  async aprovarDefesa(id: number): Promise<RespostaDefesaResponse> {
    const { data } = await api.post<RespostaDefesaResponse>(
      `/defesas/${id}/responder`,
      {
        acao: 'APROVAR'
      } as RespostaDefesaRequest
    );
    return data;
  },

  async rejeitarDefesa(id: number, justificativa: string): Promise<RespostaDefesaResponse> {
    const { data } = await api.post<RespostaDefesaResponse>(
      `/defesas/${id}/responder`,
      {
        acao: 'REJEITAR',
        justificativa
      } as RespostaDefesaRequest
    );
    return data;
  },

  async atualizarBanca(id: number, dados: {
    data_hora?: string;
    membros?: Array<{
      papel: string;
      professor_id: number;
    }>;
  }): Promise<Defesa> {
    const { data } = await api.put<{ data: Defesa }>(`/defesas/${id}/atualizar-banca`, dados);
    return data.data;
  },

  async downloadComposicaoBanca(id: number): Promise<Blob> {
    const { data } = await api.get(`/defesas/${id}/banca/composicao/download`, {
      responseType: 'blob'
    });
    return data;
  },

  async downloadDeclaracaoBanca(id: number): Promise<Blob> {
    const { data } = await api.get('/defesas/' + id + '/banca/declaracao/download', {
      responseType: 'blob'
    });
    return data;
  },

  async obterNotas(id: number): Promise<NotasDefesa> {
    const { data } = await api.get<{ data: NotasDefesa }>(`/defesas/${id}/notas`);
    return data.data;
  },

  async registrarNotas(id: number, payload: RegistrarNotasPayload): Promise<Defesa> {
    const formData = new FormData();
    formData.append('resultado', payload.resultado);
    payload.notas.forEach((n, i) => {
      formData.append(`notas[${i}][membro_id]`, String(n.membro_id));
      if (n.aluno_id) {
        formData.append(`notas[${i}][aluno_id]`, String(n.aluno_id));
      }
      formData.append(`notas[${i}][nota]`, String(n.nota));
    });
    if (payload.arquivo_correcoes) {
      formData.append('arquivo_correcoes', payload.arquivo_correcoes);
    }
    const { data } = await api.post<{ data: Defesa }>(`/defesas/${id}/notas`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data.data;
  },

  async realizarDefesa(id: number): Promise<Defesa> {
    const { data } = await api.put<{ data: Defesa }>(`/defesas/${id}/realizar`);
    return data.data;
  },

  async enviarVersaoFinal(trabalhoId: number): Promise<Defesa> {
    const { data } = await api.post<{ data: Defesa }>(`/trabalhos/${trabalhoId}/enviar-versao-final`);
    return data.data;
  },

  async aceitarVersaoFinal(defesaId: number): Promise<Defesa> {
    const { data } = await api.post<{ data: Defesa }>(`/defesas/${defesaId}/aceitar-versao-final`);
    return data.data;
  },

  async downloadVersaoFinal(defesaId: number): Promise<Blob> {
    const { data } = await api.get(`/defesas/${defesaId}/download-versao-final`, {
      responseType: 'blob'
    });
    return data;
  },

  async downloadCorrecoes(defesaId: number): Promise<Blob> {
    const { data } = await api.get(`/defesas/${defesaId}/download-correcoes`, {
      responseType: 'blob'
    });
    return data;
  },

  async downloadAtaDefesa(defesaId: number): Promise<Blob> {
    const { data } = await api.get(`/defesas/${defesaId}/download-ata`, {
      responseType: 'blob'
    });
    return data;
  },

  async obterGravacao(id: number): Promise<{ url: string; mp4_url: string | null }> {
    const { data } = await api.get<{ url: string; mp4_url: string | null }>(`/defesas/${id}/gravacao`);
    return data;
  }
};

export interface RegistrarNotasPayload {
  resultado: 'APROVADO' | 'APROVADO_COM_CORRECOES' | 'REPROVADO';
  notas: Array<{
    membro_id: number;
    aluno_id?: number;
    nota: number;
  }>;
  arquivo_correcoes?: File;
}

export default defesaService;
